import { ARTICLE_IMPORT_TYPES, DEFAULT_ARTICLE_IMPORT_TYPE, DEFAULT_ARTICLE_IMPORT_LEDGER } from "./articleImportDefaults";
import { getImportBarcodeError, normalizeImportBarcode } from "./articleImportBarcodes";

export const LOOKUP_FIELDS = {
  group: { list: "groups", id: "GroupID", name: "GroupName", label: "Grupi" },
  category: { list: "categories", id: "CategoryID", name: "CategoryName", label: "Kategoria" },
  unit: { list: "units", id: "UnitID", name: "UnitName", label: "Njësia matëse" },
  vat: { list: "vatRates", id: "VATID", name: "VATName", label: "TVSH" },
};

export const normalizeImportName = value => String(value ?? "").trim().replace(/\s+/g, " ").toLocaleLowerCase("sq");

export function createImportLookupIndexes(lookups) {
  return Object.fromEntries(Object.entries(LOOKUP_FIELDS).map(([field, spec]) => {
    const index = new Map();
    (lookups[spec.list] || []).forEach(item => {
      const name = normalizeImportName(item[spec.name]);
      const id = Number(item[spec.id]);
      if (!name || !Number.isInteger(id) || id <= 0) return;
      const ids = index.get(name) || new Set();
      ids.add(id);
      index.set(name, ids);
    });
    return [field, index];
  }));
}

export function parseImportPrice(value, defaultValue) {
  const text = String(value ?? "").trim();
  if (!text) return defaultValue;
  // Accept decimal comma or point; do not guess thousands separators.
  if (!/^\d+(?:[.,]\d+)?$/.test(text)) return null;
  const number = Number(text.replace(",", "."));
  return Number.isFinite(number) && number >= 0 ? number : null;
}

export function validateImportRow(row, indexes) {
  const errors = { ...row.sourceIssues };
  const resolved = {};
  const barcode = normalizeImportBarcode(row.barcode);
  const name = String(row.name ?? "").trim();
  const barcodeError = getImportBarcodeError(barcode);
  if (barcodeError && !errors.barcode) errors.barcode = barcodeError;
  if (!name) errors.name = "Emërtimi është i detyrueshëm.";
  else if (name.length > 1000) errors.name = "Emërtimi lejohet deri në 1000 karaktere.";
  if (String(row.description || "").length > 800) errors.description = "Përshkrimi lejohet deri në 800 karaktere.";
  Object.entries(LOOKUP_FIELDS).forEach(([field, spec]) => {
    const value = normalizeImportName(row[field]);
    const ids = indexes[field].get(value);
    if (!value) errors[field] = `${spec.label} është e detyrueshme.`;
    else if (!ids?.size) errors[field] = `${spec.label} nuk u gjet në listën aktuale të Accountree.`;
    else if (ids.has(Number(row.lookupOverrides?.[field]))) resolved[field] = Number(row.lookupOverrides[field]);
    else if (ids.size > 1) errors[field] = "Ky emër ka disa përputhje; zgjidhni vlerën e saktë në tabelë.";
    else resolved[field] = [...ids][0];
  });
  ["purchasePrice", "salesPrice"].forEach(field => {
    const value = parseImportPrice(row[field], field === "purchasePrice" ? 0 : null);
    if (value == null) errors[field] = "Shënoni çmim zero ose pozitiv, pa ndarës mijëshe.";
    else resolved[field] = value;
  });
  const active = normalizeImportName(row.active);
  if (!["", "po", "jo"].includes(active)) errors.active = "Përdorni Po ose Jo.";
  else resolved.active = active !== "jo";
  if (!ARTICLE_IMPORT_TYPES.some(type => type.value === Number(row.articleProduction)))
    errors.articleProduction = "Zgjidhni një lloj të vlefshëm.";
  return { ...row, barcode, resolved, errors };
}

export function createImportRow(values, excelRow, sourceIssues = {}) {
  return {
    ...values, id: excelRow, excelRow, sourceIssues,
    articleProduction: DEFAULT_ARTICLE_IMPORT_TYPE,
    ledgerId: DEFAULT_ARTICLE_IMPORT_LEDGER,
  };
}

export function findImportDuplicates(rows) {
  const barcodes = new Map();
  const names = new Map();
  rows.forEach(row => {
    const barcode = normalizeImportName(row.barcode);
    const name = normalizeImportName(row.name);
    if (barcode) barcodes.set(barcode, (barcodes.get(barcode) || 0) + 1);
    if (name) names.set(name, (names.get(name) || 0) + 1);
  });
  return { barcodes, names };
}

export function getImportRowErrors(row, duplicates) {
  const errors = { ...row.errors };
  if (duplicates.barcodes.get(normalizeImportName(row.barcode)) > 1)
    errors.barcode = "Barkodi përsëritet në këtë Excel.";
  if (duplicates.names.get(normalizeImportName(row.name)) > 1)
    errors.name = "Emërtimi përsëritet në këtë Excel.";
  return errors;
}
